import { Application } from '@platform/client';
import { ProfessionalApplicationParameters } from '@platform/client/schemas';
import { ErrorBox, FormsInputDropdown, InputDropdownItem } from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/Aanu-Gopald.svg';

type FormValues = {
  referralMethod: string;
};

const EY_REFERRAL_OPTIONS: InputDropdownItem[] = [
  { text: 'A colleague within your company', textValue: 'A colleague within your company' },
  { text: 'Your manager', textValue: 'Your manager' },
  { text: 'Your Global CSR leader', textValue: 'Your Global CSR leader' },
  { text: 'Your Regional CSR leader', textValue: 'Your Regional CSR leader' },
  {
    text: 'Email communication from your company',
    textValue: 'Email communication from your company',
  },
  { text: 'Internal meeting or town hall', textValue: 'Internal meeting or town hall' },
  {
    text: 'An internal organization tool or website',
    textValue: 'An internal organization tool or website',
  },
  { text: 'LinkedIn', textValue: 'LinkedIn' },
  { text: 'Other', textValue: 'Other' },
];

export const EYSubmission = (props: ApplicationPageProps) => {
  const { onBack, progress, dataKey } = { ...props };
  const [failed, setFailed] = useState(false);

  const { getApplicationData } =
    useApplicationLocalStorage<ProfessionalApplicationParameters>(dataKey);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({ mode: 'onBlur', reValidateMode: 'onChange' });

  const submitEYMutation = Application.useSubmitEyApplication();
  const queryClient = useQueryClient();

  const submit: SubmitHandler<FormValues> = (data) => {
    submitEYMutation.mutate(
      {
        data: {
          ...getApplicationData(),
          referralMethod: data.referralMethod,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: Application.getGetProfessionalApplicationStatusQueryKey(),
          });
        },
        onError: () => {
          setFailed(true);
        },
      },
    );
  };

  return (
    <ApplicationBackground
      progress={progress}
      onBack={onBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
    >
      <ApplicationPageContent
        title='Ready to submit? Just one more thing.'
        description='After you submit, your responses are final, so take a moment to make sure everything is just right.'
      >
        <FormsInputDropdown
          control={control}
          name='referralMethod'
          label='How did you find out about this opportunity?'
          placeholder='Select one'
          items={EY_REFERRAL_OPTIONS}
          type='default'
          error={errors.referralMethod}
          inputDisabled={true}
        />
        {failed && (
          <ErrorBox showAlertTriangle={true}>
            There was an error submitting your application. Please try again later.
          </ErrorBox>
        )}
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
